<script setup lang="ts">
import Toolbar from '@/components/toolbar/index.vue'
import type { ECharts } from 'echarts'

/**
 * Use framework.
 */
const { find } = useFramework()

/**
 * Use the query params.
 */
const { config } = useParams()

/**
 * Current dimension.
 */
const dimension = computed(() => {
    return find(config.dimension)
})

/**
 * Reactive page title.
 */
const title = computed(() => {
    return unref(dimension)?.name || ''
})

/**
 * Import data.
 */
const data = reactive(useValues<Import>(dimension, true))

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * ECharts instance.
 */
const instance = shallowRef<ECharts>()

/**
 * Filter values based on selected products.
 */
const filteredValues = computed(() => {
    return data.data?.map(value => ({
        ...value,
        products: value.products.filter(product => {
            if (!config.importProducts?.length) return true
            return config.importProducts?.includes(product.product_id)
        })
    }))
})

watch(
    () => [instance.value, data.loading] as const,
    ([instance, loading]) => {
        if (!instance) return

        if (loading) {
            instance.showLoading()
        } else {
            instance.hideLoading()
        }
    }
)

/**
 * Update title based on the current dimension.
 */
useTitle(title, {
    titleTemplate(title) {
        return title ? `${title} | ${t('title')}` : t('title')
    }
})
</script>

<template>
    <UiContainer
        class="flex flex-1 flex-col gap-1 overflow-hidden xl:gap-2 xl:screen-ratio-[2:1]:gap-1"
    >
        <UiHeading :title="title" :description="dimension?.description" />

        <HomeImportingRiskProductsSelector
            v-model:products="config.importProducts"
            class="my-2 lg:mb-4"
        />

        <div
            class="flex flex-1 flex-col gap-2 overflow-hidden lg:grid lg:divide-x"
            :class="[config.compareCountry ? 'grid-cols-4' : 'grid-cols-3']"
        >
            <HomeImportingRiskGlobalData
                v-model:country="config.compareCountry"
                :values="filteredValues"
                class="col-span-3 w-full overflow-hidden"
                @ready="instance = $event"
            />

            <HomeImportingRiskCountryData
                :country="config.compareCountry"
                :values="filteredValues"
            />
        </div>
    </UiContainer>

    <Toolbar :title="title" :instance="instance" :dimension="dimension">
        <ToolbarG20Country />
        <ToolbarImportProducts :dimension="dimension" />
    </Toolbar>
</template>